/**
 * secureStore.ts
 * 端末内の機微データ(オフライン認証パック・顔特徴量など)を localStorage に
 * 保存する際の AES-GCM 暗号化ユーティリティ。
 *
 * 鍵は端末固有で localStorage に保持する(useFaceAuth の顔特徴量暗号化と同じ鍵を共有)。
 * 鍵がデータと同じ端末にあるため端末を完全に掌握した攻撃への防御は限定的だが、
 * ストレージダンプ・バックアップ抽出等からの平文漏えいを防ぐ。
 * ※ オフラインでも復号できる必要があるため、鍵をサーバー側に置くことはできない。
 *
 * 保存形式: "sec1:<iv(base64)>.<ciphertext(base64)>"
 */

const DEVICE_KEY_STORAGE = 'CATMS_FACE_FEATURES_ENC';
const PREFIX = 'sec1:';

async function getDeviceKey(): Promise<CryptoKey> {
  let rawKey = localStorage.getItem(DEVICE_KEY_STORAGE);
  if (!rawKey) {
    const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
    const exported = await crypto.subtle.exportKey('raw', key);
    rawKey = btoa(String.fromCharCode(...new Uint8Array(exported)));
    localStorage.setItem(DEVICE_KEY_STORAGE, rawKey);
  }
  const keyData = Uint8Array.from(atob(rawKey), c => c.charCodeAt(0));
  return crypto.subtle.importKey('raw', keyData, 'AES-GCM', false, ['encrypt', 'decrypt']);
}

export function isSecureString(stored: string): boolean {
  return stored.startsWith(PREFIX);
}

export async function encryptString(plain: string): Promise<string> {
  const key = await getDeviceKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const enc = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(plain));
  return PREFIX + btoa(String.fromCharCode(...iv)) + '.' + btoa(String.fromCharCode(...new Uint8Array(enc)));
}

/** 復号。形式不正・鍵不一致は null を返す(呼び出し側で空扱いにする)。 */
export async function decryptString(stored: string): Promise<string | null> {
  if (!isSecureString(stored)) return null;
  try {
    const [ivB64, dataB64] = stored.slice(PREFIX.length).split('.');
    const key = await getDeviceKey();
    const iv = Uint8Array.from(atob(ivB64), c => c.charCodeAt(0));
    const data = Uint8Array.from(atob(dataB64), c => c.charCodeAt(0));
    const dec = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data);
    return new TextDecoder().decode(dec);
  } catch {
    return null;
  }
}
